import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import { Component, effect, ElementRef, HostBinding, input, OnDestroy, output, signal, viewChild } from '@angular/core';
import { UntypedFormControl, ValidationErrors } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Validator } from '@vendasta/shared';
import { CrmDynamicFieldService } from '../../services/crm-dynamic-field.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

declare let tinymce: any;

const tinyMCEHyperlinkTextColour = '#057EC1';

@Component({
  selector: 'email-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  imports: [MatProgressSpinnerModule],
})
export class RichTextEditorComponent implements OnDestroy {
  @HostBinding('class') class = 'email-rich-text-editor';
  private readonly richEditorDiv = viewChild.required<ElementRef<HTMLElement>>('richEditorDiv');

  constructor(
    private readonly crmDynamicFieldService: CrmDynamicFieldService,
    private readonly snackbarService: SnackbarService,
  ) {
    effect(() => {
      const html = this.humanReadableContents();
      void html; // unused but needed to trigger the effect
      if (!this.editor) {
        this.reloadEditor();
      }
    });
  }

  value = input<string>();
  protected readonly humanReadableContents = toSignal(
    this.crmDynamicFieldService.convertTemplateTagToHumanReadable$(toObservable(this.value)),
  );
  valueChange = output<string>();
  statusChange = output<ValidationErrors | null>();
  protected readonly editorIsReady = signal(false);

  private editor: any;
  private readonly theme =
    window.getComputedStyle(document.documentElement).getPropertyValue('--is-dark-theme') === 'true' ? 'dark' : 'light';

  ngOnDestroy(): void {
    if (this.editor) {
      tinymce.remove(this.editor);
    }
  }

  private onEditorChange(): void {
    if (!this.editor) {
      return;
    }

    const newContent = this.editor.getContent();
    const convertedContent = this.crmDynamicFieldService.convertHumanReadableToTemplateTag(newContent);
    const currentValue = this.value() || '';
    if (convertedContent === currentValue) {
      return;
    } else {
      this.valueChange.emit(convertedContent);

      if (convertedContent !== '') {
        const isValid = Validator.containsValidDynamicContent({ value: convertedContent } as UntypedFormControl);
        this.statusChange.emit(isValid);
      }
    }
  }

  public reloadEditor(): void {
    if (this.editor) {
      tinymce.remove(this.editor);
    }
    this.editor = null;
    this.editorIsReady.set(false);

    window.setTimeout(() => {
      const content = this.humanReadableContents();
      if (content !== undefined) {
        this.setupEditor(content);
      }
    }, 0);
  }

  public insertAtCursor(text: string): void {
    this.crmDynamicFieldService.convertTemplateTagToHumanReadable(text).then(
      (html: string) => {
        this.editor.insertContent(html);
      },
      (_) => {
        this.snackbarService.openErrorSnack('EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.ERRORS.FAILED_TO_ADD_CONTENTS');
      },
    );
  }

  public focus(): void {
    this.richEditorDiv().nativeElement.focus();
  }

  private setupEditor(html: string): void {
    tinymce.baseURL = 'https://storage.googleapis.com/developers-vendasta-client/assets';
    tinymce.init({
      browser_spellcheck: true,
      content_style: `a {color: ${tinyMCEHyperlinkTextColour}`,
      skin: this.theme === 'dark' ? 'glxy-dark' : 'oxide',
      content_css: this.theme === 'dark' ? 'glxy-dark' : 'default',
      target: this.richEditorDiv().nativeElement,
      readonly: false,
      width: 600,
      height: '100%',
      menubar: false,
      menu: false,
      toolbar: ['bold italic link unlink | alignleft aligncenter alignright | bullist numlist | code removeformat'],
      plugins: 'lists link code image',
      convert_urls: false,
      allow_conditional_comments: true,
      valid_children: '+body[style]',
      invalid_elements: null,
      branding: false,
      setup: (editor) => {
        this.editor = editor;
        editor.on('init', () => {
          this.editor.setContent(html || '');
        });
        // prevents this.editor from being undefined on events
        const updateHandler = () => {
          this.onEditorChange();
        };
        editor.on('keyup', updateHandler);
        editor.on('change', updateHandler);
        editor.on('NodeChange', updateHandler);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      images_upload_handler: function (blobInfo: any, success: any, failure: any): void {
        //
      },
      images_upload_url: '',
      init_instance_callback: () => {
        this.editorIsReady.set(true);
      },
    });
  }
}
